/**
 * Meetings slice - single source for meetings API
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/config';
import {  MEETINGS } from '../../api/endpoints';

export const getMeetings = createAsyncThunk(
  'meetings/getMeetings',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(MEETINGS.LIST, { params });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch meetings');
    }
  }
);

export const getMeetingById = createAsyncThunk(
  'meetings/getMeetingById',
  async (meetingId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(MEETINGS.BY_ID(meetingId));
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch meeting');
    }
  }
);

export const createMeeting = createAsyncThunk(
  'meetings/createMeeting',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(MEETINGS.LIST, payload);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to create meeting');
    }
  }
);

export const updateMeeting = createAsyncThunk(
  'meetings/updateMeeting',
  async ({ meetingId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(MEETINGS.BY_ID(meetingId), payload);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update meeting');
    }
  }
);

export const updateMeetingStatus = createAsyncThunk(
  'meetings/updateMeetingStatus',
  async ({ meetingId, payload }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(MEETINGS.UPDATE_STATUS(meetingId), payload);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update meeting status');
    }
  }
);

const initialState = {
  meetings: null,
  currentMeeting: null,
  loading: false,
  error: null,
};

const meetingsSlice = createSlice({
  name: 'meetings',
  initialState,
  reducers: {
    clearMeetingsError: (state) => {
      state.error = null;
    },
    clearCurrentMeeting: (state) => {
      state.currentMeeting = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMeetings.fulfilled, (state, action) => {
        state.loading = false;
        state.meetings = action.payload;
      })
      .addCase(getMeetingById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentMeeting = action.payload?.data ?? action.payload;
      })
      .addCase(createMeeting.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateMeeting.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateMeetingStatus.fulfilled, (state) => {
        state.loading = false;
      })
      .addMatcher(
        (a) => [getMeetings.pending, getMeetingById.pending, createMeeting.pending, updateMeeting.pending, updateMeetingStatus.pending].some((t) => t.match(a)),
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )
      .addMatcher(
        (a) => [getMeetings.rejected, getMeetingById.rejected, createMeeting.rejected, updateMeeting.rejected, updateMeetingStatus.rejected].some((t) => t.match(a)),
        (state, action) => {
          state.loading = false;
          state.error = action.payload || 'Something went wrong';
        }
      );
  },
});

export const { clearMeetingsError, clearCurrentMeeting } = meetingsSlice.actions;
export default meetingsSlice.reducer;
